import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listCfCodes, createCfCode, updateCfCode, deactivateCfCode, getMappingStatus, refreshMappingStatus } from '@/api/cfCodes';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Table, { type Column } from '@/components/ui/Table';
import IconButton from '@/components/ui/IconButton';
import { Pencil, Archive, RefreshCw } from 'lucide-react';
import type { CfCode, CfLineType, CfStatementSection, SignConvention } from '@/types';
import { sectionLabel } from '@/utils/format';

const lineTypeOptions = [
  { value: 'DERIVED', label: 'Derived (from Qlik)' },
  { value: 'CLASSIFIED', label: 'Classified (entity entry)' },
];

const sectionOptions: { value: CfStatementSection; label: string }[] = [
  { value: 'OPERATING', label: 'Operating Profit & Non-Cash Adjustments' },
  { value: 'WORKING_CAPITAL_DEBTORS', label: 'Working Capital — Debtors' },
  { value: 'AMOUNTS_DUE_FROM_GROUP', label: 'Amounts Due from Group' },
  { value: 'WORKING_CAPITAL_CREDITORS', label: 'Working Capital — Creditors' },
  { value: 'FINANCE_MOVEMENTS', label: 'Finance Movements' },
  { value: 'INVESTING', label: 'Investing Activities' },
  { value: 'FINANCING', label: 'Financing Activities' },
];

const signOptions = [
  { value: 'POSITIVE', label: 'Positive (cash inflow)' },
  { value: 'NEGATIVE', label: 'Negative (cash outflow)' },
];

interface CfCodeForm {
  code: string;
  label: string;
  band: string;
  lineType: CfLineType;
  statementSection: CfStatementSection;
  signConvention: SignConvention;
  sortOrder: string;
  notes: string;
}

const emptyForm: CfCodeForm = {
  code: '',
  label: '',
  band: '',
  lineType: 'CLASSIFIED',
  statementSection: 'INVESTING',
  signConvention: 'NEGATIVE',
  sortOrder: '100',
  notes: '',
};

export default function CfCodeAdminPage() {
  const qc = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CfCodeForm>(emptyForm);
  const [formError, setFormError] = useState('');

  const [unmappedOnly, setUnmappedOnly] = useState(false);

  // Which codes the Qlik cash-flow report actually maps (snapshot from Qlik).
  const { data: mapping } = useQuery({
    queryKey: ['cf-code-mapping-status'],
    queryFn: getMappingStatus,
  });
  const unmappedCodes = new Set((mapping?.unmapped ?? []).map((u) => u.code));
  const refreshMapping = useMutation({
    mutationFn: refreshMappingStatus,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cf-code-mapping-status'] }),
  });

  const { data: codes, isLoading } = useQuery({
    queryKey: ['cf-codes', showInactive],
    queryFn: () => listCfCodes(showInactive),
  });

  const createMutation = useMutation({
    mutationFn: createCfCode,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cf-codes'] });
      setModalOpen(false);
      setForm(emptyForm);
    },
    onError: (err: unknown) => {
      setFormError(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Save failed',
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CfCode> }) => updateCfCode(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cf-codes'] });
      setModalOpen(false);
      setEditingId(null);
      setForm(emptyForm);
    },
    onError: (err: unknown) => {
      setFormError(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Save failed',
      );
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: deactivateCfCode,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cf-codes'] }),
  });

  const openEdit = (code: CfCode) => {
    setEditingId(code.id);
    setForm({
      code: code.code,
      label: code.label,
      band: code.band,
      lineType: code.lineType,
      statementSection: code.statementSection,
      signConvention: code.signConvention,
      sortOrder: String(code.sortOrder),
      notes: code.notes ?? '',
    });
    setFormError('');
    setModalOpen(true);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError('');
    setModalOpen(true);
  };

  const handleSubmit = () => {
    setFormError('');
    const payload = {
      code: form.code.trim().toUpperCase(),
      label: form.label.trim(),
      band: form.band.trim(),
      lineType: form.lineType,
      statementSection: form.statementSection,
      signConvention: form.signConvention,
      sortOrder: parseInt(form.sortOrder, 10) || 100,
      isActive: true,
      isProvisional: false,
      notes: form.notes.trim() || null,
    };
    if (!payload.code || !payload.label || !payload.band) {
      setFormError('Code, label and band are required.');
      return;
    }
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const columns: Column<CfCode>[] = [
    {
      key: 'code',
      header: 'Code',
      sortable: true,
      render: (c) => (
        <span className="font-mono text-sm font-semibold text-gray-900">{c.code}</span>
      ),
    },
    {
      key: 'label',
      header: 'Label',
      render: (c) => (
        <div>
          <span className="text-gray-800">{c.label}</span>
          {c.isProvisional && (
            <Badge variant="amber" className="ml-2">Provisional</Badge>
          )}
          {unmappedCodes.has(c.code) && (
            <span title="Not in the Qlik report mapping — figures on this code will not appear in Qlik">
              <Badge variant="red" className="ml-2">Not in Qlik mapping</Badge>
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'section',
      header: 'Section',
      render: (c) => <span className="text-gray-600 text-xs">{sectionLabel(c.statementSection)}</span>,
    },
    {
      key: 'lineType',
      header: 'Type',
      render: (c) => (
        <Badge variant={c.lineType === 'CLASSIFIED' ? 'blue' : 'grey'}>
          {c.lineType === 'CLASSIFIED' ? 'Classified' : 'Derived'}
        </Badge>
      ),
    },
    {
      key: 'sign',
      header: 'Sign',
      render: (c) => (
        <span className="text-xs text-gray-500">
          {c.signConvention === 'POSITIVE' ? '+' : '−'}
        </span>
      ),
    },
    {
      key: 'sortOrder',
      header: 'Order',
      sortable: true,
      render: (c) => <span className="text-gray-500">{c.sortOrder}</span>,
    },
    {
      key: 'active',
      header: 'Status',
      render: (c) => (
        <Badge variant={c.isActive ? 'green' : 'grey'}>
          {c.isActive ? 'Active' : 'Retired'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (c) => (
        <div className="flex items-center gap-1">
          <IconButton
            icon={Pencil}
            label="Edit code"
            variant="primary"
            onClick={(e) => { e.stopPropagation(); openEdit(c); }}
          />
          {c.isActive && (
            <IconButton
              icon={Archive}
              label="Retire code"
              variant="danger"
              onClick={(e) => { e.stopPropagation(); deactivateMutation.mutate(c.id); }}
            />
          )}
        </div>
      ),
    },
  ];

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CF Code List</h1>
          <p className="mt-1 text-sm text-gray-500">
            Controlled list of cash flow codes. Codes are never deleted — retire them instead.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {mapping && mapping.capturedAt && (
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={unmappedOnly}
                onChange={(e) => setUnmappedOnly(e.target.checked)}
                className="rounded border-gray-300"
              />
              Unmapped only
            </label>
          )}
          <Button
            size="sm"
            variant="secondary"
            loading={refreshMapping.isPending}
            onClick={() => refreshMapping.mutate()}
            title="Re-read the report mapping from the live Qlik app"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Check Qlik mapping
          </Button>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show retired
          </label>
          <Button size="sm" onClick={openCreate}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Add code
          </Button>
        </div>
      </div>

      {mapping && mapping.capturedAt && mapping.unmapped.length > 0 && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="font-semibold">
            {mapping.unmapped.length} active classified code{mapping.unmapped.length === 1 ? ' is' : 's are'} not in the Qlik report mapping
          </p>
          <p className="mt-1">
            Figures entered against these codes load into Qlik but never appear on the cash flow report.
            Add them to the mapping workbook (CFlow_Group sheet) or retire them:{' '}
            <span className="font-mono">{mapping.unmapped.map((u) => u.code).join(', ')}</span>.
          </p>
          {mapping.exposure.lines > 0 && (
            <p className="mt-1">
              Currently affecting <strong>{mapping.exposure.lines}</strong> submitted/agreed line{mapping.exposure.lines === 1 ? '' : 's'} totalling{' '}
              <strong>£{(Number(mapping.exposure.absPence) / 100).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</strong> (absolute).
            </p>
          )}
          <p className="mt-1 text-xs text-red-700">
            Qlik mapping last checked {new Date(mapping.capturedAt).toLocaleString('en-GB')} · {mapping.mappedCount} codes mapped.
          </p>
        </div>
      )}
      {mapping && mapping.capturedAt && mapping.unmapped.length === 0 && (
        <p className="text-xs text-gray-500">
          All active classified codes are in the Qlik report mapping · last checked {new Date(mapping.capturedAt).toLocaleString('en-GB')}.
        </p>
      )}
      {mapping && !mapping.capturedAt && (
        <p className="text-xs text-amber-700">
          Qlik report mapping has not been checked yet — it refreshes automatically on each Qlik sync, or use "Check Qlik mapping".
        </p>
      )}

      <Table
        columns={columns}
        data={unmappedOnly ? (codes ?? []).filter((c) => unmappedCodes.has(c.code)) : (codes ?? [])}
        loading={isLoading}
        rowKey={(c) => c.id}
        emptyMessage="No CF codes found."
      />

      <Modal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingId(null); }}
        title={editingId ? 'Edit CF code' : 'Add CF code'}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button loading={isSaving} onClick={handleSubmit}>
              {editingId ? 'Save changes' : 'Create code'}
            </Button>
          </>
        }
      >
        {!editingId && (
          <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            New codes must also be added to the Qlik report mapping workbook (CFlow_Group sheet) or
            figures entered against them will not appear in Qlik. This page flags unmapped codes.
          </p>
        )}
        <div className="space-y-4">
          {formError && (
            <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
              {formError}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Code (e.g. CF5100)"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              placeholder="CF5100"
              disabled={!!editingId}
            />
            <Input
              label="Band (e.g. CF5xxx)"
              value={form.band}
              onChange={(e) => setForm((f) => ({ ...f, band: e.target.value }))}
              placeholder="CF5xxx"
            />
          </div>
          <Input
            label="Label"
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            placeholder="e.g. Capital Expenditure"
          />
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Line type"
              value={form.lineType}
              onChange={(e) => setForm((f) => ({ ...f, lineType: e.target.value as CfLineType }))}
              options={lineTypeOptions}
            />
            <Select
              label="Sign convention"
              value={form.signConvention}
              onChange={(e) => setForm((f) => ({ ...f, signConvention: e.target.value as SignConvention }))}
              options={signOptions}
            />
          </div>
          <Select
            label="Statement section"
            value={form.statementSection}
            onChange={(e) => setForm((f) => ({ ...f, statementSection: e.target.value as CfStatementSection }))}
            options={sectionOptions}
          />
          <Input
            label="Sort order"
            type="number"
            value={form.sortOrder}
            onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
          />
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="Optional notes for this code"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
